/**
 * Exam Convocation Service
 * API client for exam convocation endpoints
 */

import apiClient from './apiClient';
import {
  ConvocationListParams,
  ConvocationListResponse,
  ConvocationSummary,
} from '../types/examConvocation';

class ExamConvocationService {
  private baseUrl = '/exam-convocations';

  /** Build the query string shared by list and export endpoints. */
  private buildQuery(params: ConvocationListParams): URLSearchParams {
    const queryParams = new URLSearchParams();
    queryParams.set('companyId', String(params.companyId));
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.status) queryParams.set('status', params.status);
    if (params.departmentId) queryParams.set('departmentId', String(params.departmentId));
    if (params.occupationId) queryParams.set('occupationId', String(params.occupationId));
    if (params.search) queryParams.set('search', params.search);
    if (params.expiringDays) queryParams.set('expiringDays', String(params.expiringDays));
    return queryParams;
  }

  async getConvocations(params: ConvocationListParams): Promise<ConvocationListResponse> {
    const queryParams = this.buildQuery(params);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`) as any;

    return {
      items: response.data || [],
      total: response.pagination?.total || 0,
      summary: response.summary || { totalOverdue: 0, totalExpiring: 0, totalOk: 0, totalEmployeesAffected: 0 },
      page: response.pagination?.page || 1,
      totalPages: response.pagination?.totalPages || 1,
    };
  }

  async getSummary(companyId?: number): Promise<ConvocationSummary> {
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.set('companyId', String(companyId));

    const response = await apiClient.get(`${this.baseUrl}/summary?${queryParams.toString()}`);
    return response.data.data || { totalOverdue: 0, totalExpiring: 0, totalOk: 0, totalEmployeesAffected: 0 };
  }

  /** Export the (filtered) convocation list as an Excel file. */
  async exportExcel(params: ConvocationListParams): Promise<Blob> {
    const queryParams = this.buildQuery(params);
    queryParams.delete('page');
    queryParams.delete('limit');
    const response = await apiClient.get(`${this.baseUrl}/export/excel?${queryParams.toString()}`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  /** Export the (filtered) convocation list as a PDF report. */
  async exportPdf(params: ConvocationListParams): Promise<Blob> {
    const queryParams = this.buildQuery(params);
    queryParams.delete('page');
    queryParams.delete('limit');
    const response = await apiClient.get(`${this.baseUrl}/export/pdf?${queryParams.toString()}`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }
}

export const examConvocationService = new ExamConvocationService();
export default examConvocationService;
